import { ReactNode, useRef, useState } from 'react';
import ComboBoxField from '@/components/select/ComboBoxField';
import { AdditiveComboBoxProps, ComboBoxItem } from '@/components/select/comboBoxTypes';
import useComboBoxQuery from '@/components/select/useComboBoxQuery';
import { Combobox } from '@headlessui/react';

const AdditiveComboBox = <T extends ComboBoxItem>({
  name,
  label,
  hideLabel,
  placeholder,
  queryKey = name ? [name] : [],
  queryFn,
  onAdd,
  selectedIds = [],
  disabled = false,
  delay = 500,
  closeOnBlur = true,
  openOnFocus = true,
  showEmptyState = false,
}: AdditiveComboBoxProps<T>): ReactNode => {
  const isInitializedRef = useRef(true);
  const [emptyStateVisible, setEmptyStateVisible] = useState(true);
  const comboBox = useComboBoxQuery<T>({
    queryKey,
    queryFn,
    isInitializedRef,
    delay,
    closeOnBlur,
    openOnFocus,
    keepPreviousResults: true,
  });
  const selectedIdSet = new Set(selectedIds.map(String));
  const availableItems = comboBox.items.filter((item) => !selectedIdSet.has(String(item.id)));

  const handleChange = (selected: T[]) => {
    const addedItem = selected.at(-1);
    if (!addedItem) return;

    if (availableItems.length === 1) {
      setEmptyStateVisible(false);
      comboBox.inputRef.current?.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }),
      );
    }

    onAdd(addedItem);
  };

  const handleClearQuery = () => {
    comboBox.clearQuery();

    setTimeout(() => {
      comboBox.inputRef.current?.blur();
      comboBox.inputRef.current?.focus();
    });
  };

  return (
    <Combobox
      value={Array<T>()}
      onChange={handleChange}
      disabled={disabled}
      immediate={openOnFocus}
      multiple
    >
      {({ open }) => (
        <ComboBoxField<T>
          label={label}
          hideLabel={hideLabel}
          placeholder={placeholder}
          inputRef={comboBox.inputRef}
          open={open}
          items={availableItems}
          isLoading={comboBox.isLoading}
          showEmptyOption={false}
          displayValue={() => comboBox.inputValue}
          onFocus={comboBox.handleFocus}
          onBlur={comboBox.handleBlur}
          onAfterLeave={() => setEmptyStateVisible(true)}
          onDeselect={() => {}}
          onClearQuery={comboBox.inputValue ? handleClearQuery : undefined}
          showEmptyState={showEmptyState && emptyStateVisible}
          modal={false}
        />
      )}
    </Combobox>
  );
};

export default AdditiveComboBox;
